/**
 * Derived from svg-path-properties@0.2.2 vendored by flubber@0.4.2 (MIT).
 * Adapted for dependency-free internal ESM use in anvil2.
 */

import a2c from "./a2c";
import Bezier from "./bezier";

type Point = {
  x: number;
  y: number;
};

type SegmentProperties = {
  x: number;
  y: number;
  tangentX: number;
  tangentY: number;
};

type Curve = {
  getTotalLength(): number;
  getPointAtLength(length: number): Point;
  getTangentAtLength(length: number): Point;
};

class Arc {
  private readonly length: number;

  private readonly partialLengths: number[];

  private readonly curves: Curve[];

  constructor(
    x0: number,
    y0: number,
    rx: number,
    ry: number,
    xAxisRotate: number,
    largeArcFlag: number,
    sweepFlag: number,
    x: number,
    y: number,
  ) {
    let length = 0;
    const partialLengths: number[] = [];
    const curves: Curve[] = [];
    const result = a2c(
      x0,
      y0,
      rx,
      ry,
      xAxisRotate,
      largeArcFlag,
      sweepFlag,
      x,
      y,
    );

    result.forEach((curveData) => {
      const curve = Bezier(
        curveData[0],
        curveData[1],
        curveData[2],
        curveData[3],
        curveData[4],
        curveData[5],
        curveData[6],
        curveData[7],
      );
      const curveLength = curve.getTotalLength();
      length += curveLength;
      partialLengths.push(curveLength);
      curves.push(curve);
    });

    this.length = length;
    this.partialLengths = partialLengths;
    this.curves = curves;
  }

  getTotalLength() {
    return this.length;
  }

  getPointAtLength(fractionLength: number): Point {
    if (fractionLength < 0) {
      fractionLength = 0;
    } else if (fractionLength > this.length) {
      fractionLength = this.length;
    }

    let index = this.partialLengths.length - 1;

    while (
      this.partialLengths[index] >= fractionLength &&
      this.partialLengths[index] > 0
    ) {
      index -= 1;
    }

    if (index < this.partialLengths.length - 1) {
      index += 1;
    }

    let lengthOffset = 0;

    for (let cursor = 0; cursor < index; cursor += 1) {
      lengthOffset += this.partialLengths[cursor];
    }

    return this.curves[index]!.getPointAtLength(fractionLength - lengthOffset);
  }

  getTangentAtLength(fractionLength: number): Point {
    if (fractionLength < 0) {
      fractionLength = 0;
    } else if (fractionLength > this.length) {
      fractionLength = this.length;
    }

    let index = this.partialLengths.length - 1;

    while (
      this.partialLengths[index] >= fractionLength &&
      this.partialLengths[index] > 0
    ) {
      index -= 1;
    }

    if (index < this.partialLengths.length - 1) {
      index += 1;
    }

    let lengthOffset = 0;

    for (let cursor = 0; cursor < index; cursor += 1) {
      lengthOffset += this.partialLengths[cursor];
    }

    return this.curves[index]!.getTangentAtLength(
      fractionLength - lengthOffset,
    );
  }

  getPropertiesAtLength(fractionLength: number): SegmentProperties {
    const tangent = this.getTangentAtLength(fractionLength);
    const point = this.getPointAtLength(fractionLength);

    return { x: point.x, y: point.y, tangentX: tangent.x, tangentY: tangent.y };
  }
}

export default function createArc(
  x0: number,
  y0: number,
  rx: number,
  ry: number,
  xAxisRotate: number,
  largeArcFlag: number,
  sweepFlag: number,
  x: number,
  y: number,
) {
  return new Arc(x0, y0, rx, ry, xAxisRotate, largeArcFlag, sweepFlag, x, y);
}
